import { useEffect, useState } from 'react';
import { Page, Header, Content, ContentHeader, SupportButton, Table } from '@backstage/core-components';
import {
  EntityListProvider,
  useEntityList,
  EntityKindFilter,
  EntityRefLink,
  EntityTypePicker,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { requestPageTranslationRef } from '../../translation/request-page/request-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { identityApiRef, useApi, configApiRef } from '@backstage/core-plugin-api';
import { Grid, MenuItem, Select, TextField, Typography } from '@material-ui/core';

const PrefilteredTable = ({
  kind = 'Request',
  userName,
  filter,
  title,
}: {
  kind?: string;
  userName: string;
  filter?: (entity: any) => boolean;
  title: string;
}) => {
  const { updateFilters, backendEntities } = useEntityList();
  const { t } = useTranslationRef(requestPageTranslationRef);

  useEffect(() => {
    updateFilters({
      kind: new EntityKindFilter(kind, kind),
    });
  }, [kind, updateFilters]);

  // Filtra las entidades antes de pasarlas a la tabla
  const filteredEntities = filter ? backendEntities.filter(filter) : backendEntities;

  // Define columns for Table
  const columns = [
    {
      title: t('requestTableColumns.name'),
      field: 'name',
      highlight: true,
      render: (row: any) => (
        <EntityRefLink
          entityRef={row.entity}
          defaultKind={row.entity.kind ?? 'Request'}
          defaultNamespace="default"
        />
      ),
    },
    {
      title: t('requestTableColumns.requestType') || 'Request Type',
      field: 'requestType',
      render: (row: any) => row.entity.spec?.type ?? 'Component',
    },
    {
      title: t('requestTableColumns.requester'),
      field: 'requester',
      render: (row: any) => {
        const requester = row.entity.metadata.annotations?.['example.com/requested-by'];
        const requesterRef = requester ? `user:default/${requester}` : '—';
        return requester
          ? <EntityRefLink entityRef={requesterRef} defaultKind="User" />
          : '—';
      },
    },
    {
      title: t('requestTableColumns.approver'),
      field: 'approver',
      render: (row: any) => {
        const approver = row.entity.metadata.annotations?.['example.com/approved-by'];
        const approverRef = approver ? `user:default/${approver}` : '—';
        return approver
          ? <EntityRefLink entityRef={approverRef} defaultKind="User" />
          : '—';
      },
    },
    {
      title: t('requestTableColumns.application'),
      field: 'application',
      render: (row: any) => {
        const applicationRef = row.entity.spec?.application ?? '—';
        return typeof applicationRef === 'string' ? (
          <EntityRefLink entityRef={applicationRef} defaultKind="Application" />
        ) : (
          '—'
        );
      },
    },
    {
      title: t('requestTableColumns.project'),
      field: 'project',
      render: (row: any) => {
        const projectRef = row.entity.spec?.project ?? '—';
        return typeof projectRef === 'string' ? (
          <EntityRefLink entityRef={projectRef} defaultKind="Project" />
        ) : (
          '—'
        );
      },
    },
    {
      title: t('requestTableColumns.requestDate') || 'Request Date',
      field: 'requestDate',
      render: (row: any) => {
        const rawDate = row.entity.spec?.requestDate;
        if (!rawDate) return '—';


        const date = new Date(rawDate);
        return new Intl.DateTimeFormat('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }).format(date);
      },
    },
    {
      title: t('requestTableColumns.status') || 'Status',
      field: 'status',
      render: (row: any) => row.status ?? '—',
    },
  ];

  // Map entities to rows for Table
  const data = filteredEntities.map(entity => {
    const tags = entity.metadata.tags ?? [];

    const approvalTag = tags.find((tag: string) => tag.startsWith('approval-status:'));
    const provisioningTag = tags.find((tag: string) => tag.startsWith('provisioning:'));

    const approvalStatus = approvalTag ? approvalTag.split(':')[1] : undefined;
    const provisioningStatus = provisioningTag ? provisioningTag.split(':')[1] : undefined;

    const requester = entity.metadata.annotations?.['example.com/requested-by'];
    const isOwnRequest = requester === userName;

    let status: string;

    if (approvalStatus === 'pending') {
      status = isOwnRequest ? t('requestPage.status.pending') : t('requestPage.status.pending-your-approval');
    } else if (approvalStatus === 'approved') {
      if (!provisioningTag) {
        status = t('requestPage.status.provisioning-in-progress');
      } else if (provisioningStatus === 'completed') {
        status = t('requestPage.status.provisioning-completed');
      } else if (provisioningStatus === 'failed') {
        status = t('requestPage.status.provisioning-failed');
      } else if (provisioningStatus === 'pending') {
        status = t('requestPage.status.provisioning-pending');
      } else {
        status = t('requestPage.status.provisioning-unknown');
      }
    } else if (approvalStatus === 'denied') {
      status = 'Denied';
    }
    else {
      status = t('requestPage.status.unknown');
    }

    return {
      entity,
      name: entity.metadata.name,
      application: entity.spec?.system,
      project: entity.spec?.project,
      requester: entity.metadata.annotations?.['example.com/requested-by'],
      status,
    };
  });

  const sortedData = data.sort((a, b) => {
    const getPriority = (row: any) => {
      const tags = row.entity.metadata.tags ?? [];
      const approvalTag = tags.find((tag: string) => tag.startsWith('approval-status:'));
      const approvalStatus = approvalTag ? approvalTag.split(':')[1] : undefined;
      return approvalStatus === 'pending' ? 0 : 1; // 0 = pendiente (primero), 1 = lo demás
    };
    return getPriority(a) - getPriority(b);
  });


  return (
    <div style={{ width: '100%', marginBottom: 32 }}>
      <h3>{title}</h3>
      <Table
        columns={columns}
        data={sortedData}
        options={{ paging: false, search: false, toolbar: false }}
      />
    </div>
  );
};

export const RequestsPage = () => {
  const { t } = useTranslationRef(requestPageTranslationRef);
  const [userName, setUserName] = useState<string | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const config = useApi(configApiRef);
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [visibleRequestsByDelegation, setVisibleRequestsByDelegation] = useState<Set<string>>(new Set());
  const requestStatus = [
    {
      id: 'all',
      label: t('requestPage.filters.status.options.all'),
    },
    {
      id: 'pending',
      label: t('requestPage.filters.status.options.pending'),
    },
    {
      id: 'approved',
      label: t('requestPage.filters.status.options.approved'),
    },
    {
      id: 'denied',
      label: t('requestPage.filters.status.options.denied'),
    },
  ]

  useEffect(() => {
    identityApi.getBackstageIdentity().then(async identity => {
      const name = identity.userEntityRef.split('/')[1];
      
      // Check for impersonated user (for development/testing)
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedEmail = impersonatedFromStorage || impersonatedFromConfig;
      
      // Use impersonated user if available, otherwise use actual user
      let effectiveUserName = name;
      let effectiveUserEmail = '';
      
      // Get the actual user's email
      try {
        const userRef = `user:default/${name}`;
        const userEntity = await catalogApi.getEntityByRef(userRef);
        effectiveUserEmail = (userEntity?.spec?.profile as any)?.email || name;
      } catch (err) {
        console.warn('⚠️ Could not fetch user entity:', err);
        effectiveUserEmail = name;
      }
      
      // If impersonated, we need to convert email to username
      if (impersonatedEmail && impersonatedEmail !== name) {
        console.log('🎭 Using impersonated user for RequestsPage:', impersonatedEmail);
        effectiveUserEmail = impersonatedEmail;
        
        try {
          // Query catalog to find user by email
          const { items } = await catalogApi.getEntities({
            filter: {
              kind: 'User',
              'spec.profile.email': impersonatedEmail,
            },
          });
          
          if (items.length > 0) {
            // Extract username from entity ref: "user:default/username" -> "username"
            const impersonatedUserName = items[0].metadata.name;
            effectiveUserName = impersonatedUserName;
            console.log('🎭 Resolved impersonated email to username:', impersonatedUserName);
          } else {
            console.warn('⚠️ Could not find user entity for impersonated email:', impersonatedEmail);
          }
        } catch (err) {
          console.warn('⚠️ Error fetching impersonated user entity:', err);
        }
      }
      
      setUserName(effectiveUserName);
      
      // Check if user is platform_admin
      try {
        const backendUrl = config.getString('backend.baseUrl');
        const token = (await identityApi.getCredentials()).token;
        
        const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            user: `user:${effectiveUserEmail}`,
            action: 'platform_admin',
            resource: 'platform:main',
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsPlatformAdmin(data.allowed || false);
          if (data.allowed) {
            console.log('✅ User is platform_admin - showing all requests');
          }
        }
      } catch (err) {
        console.warn('⚠️ Error checking platform_admin permission:', err);
        setIsPlatformAdmin(false);
      }
    });
  }, [identityApi, catalogApi, config]);

  // Check delegation permissions for all requests
  useEffect(() => {
    const checkDelegationPermissions = async () => {
      if (!userName || isPlatformAdmin) {
        // Platform admins see everything, no need to check delegation
        return;
      }

      try {
        const backendUrl = config.getString('backend.baseUrl');
        const token = (await identityApi.getCredentials()).token;

        // Get current user's email
        const userRef = `user:default/${userName}`;
        const userEntity = await catalogApi.getEntityByRef(userRef);
        const effectiveUserEmail = (userEntity?.spec?.profile as any)?.email || userName;

        // Get all Request entities
        const { items: allRequests } = await catalogApi.getEntities({
          filter: {
            kind: 'Request',
          },
        });

        const visibleRequests = new Set<string>();

        // Check each request to see if user can view it via delegation
        for (const request of allRequests) {
          const requestName = request.metadata.name;
          const requesterName = request.metadata.annotations?.['example.com/requested-by'];
          
          // Skip if no requester or if already visible (user is requester or approver)
          if (!requesterName || requesterName === userName) {
            continue;
          }

          const approverName = request.metadata.annotations?.['example.com/approved-by'];
          if (approverName === userName) {
            continue;
          }

          try {
            // Get requester entity to find their email and manager
            const requesterRef = `user:default/${requesterName}`;
            const requesterEntity = await catalogApi.getEntityByRef(requesterRef);
            const requesterEmail = (requesterEntity?.spec?.profile as any)?.email;
            const managerUsername = requesterEntity?.metadata.labels?.['manager'];

            if (!requesterEmail || !managerUsername) {
              continue;
            }

            // Get manager entity to find their email
            const managerRef = `user:default/${managerUsername}`;
            const managerEntity = await catalogApi.getEntityByRef(managerRef);
            const managerEmail = (managerEntity?.spec?.profile as any)?.email;

            if (!managerEmail) {
              continue;
            }

            // Check if current user can approve this request using contextual tuples
            const contextualTuples = [
              {
                user: `user:${requesterEmail}`,
                relation: 'requester',
                object: `request:${requestName}`,
              },
              {
                user: `user:${managerEmail}`,
                relation: 'manager',
                object: `request:${requestName}`,
              },
            ];
            
            const response = await fetch(`${backendUrl}/api/openfga-client/check-permission-contextual`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                tuple_key: {
                  user: `user:${effectiveUserEmail}`,
                  relation: 'can_approve',
                  object: `request:${requestName}`,
                },
                contextual_tuples: {
                  tuple_keys: contextualTuples,
                },
              }),
            });

            if (response.ok) {
              const data = await response.json();
              if (data.allowed) {
                visibleRequests.add(requestName);
                console.log(`✅ User can view request ${requestName} via delegation`);
              }
            }
          } catch (err) {
            console.warn(`⚠️ Error checking delegation for request ${requestName}:`, err);
          }
        }

        setVisibleRequestsByDelegation(visibleRequests);
        console.log(`✅ Found ${visibleRequests.size} requests visible via delegation`);
      } catch (err) {
        console.warn('⚠️ Error checking delegation permissions:', err);
      }
    };

    checkDelegationPermissions();
  }, [userName, isPlatformAdmin, identityApi, catalogApi, config]);

  const isWithinDateRange = (
    requestDate: string,
    fromDate?: string | null,
    toDate?: string | null,
  ) => {
    const date = new Date(requestDate);

    if (fromDate) {
      const from = new Date(fromDate);
      if (date < from) return false;
    }

    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      if (date > to) return false;
    }

    return true;
  };

  const getRequestStatus = (entity: any) => {
    const tags = entity.metadata.tags ?? [];

    const approvalTag = tags.find((tag: string) =>
      tag.startsWith('approval-status:')
    );
    const provisioningTag = tags.find((tag: string) =>
      tag.startsWith('provisioning:')
    );

    const approvalStatus = approvalTag?.split(':')[1];
    const provisioningStatus = provisioningTag?.split(':')[1];

    if (approvalStatus === 'pending') return 'pending';
    if (approvalStatus === 'denied') return 'denied';
    if (approvalStatus === 'approved') {
      if (!provisioningStatus) return 'approved';
      if (provisioningStatus === 'pending') return 'pending';
      if (provisioningStatus === 'completed') return 'approved';
      if (provisioningStatus === 'failed') return 'approved';
    }

    return 'unknown';
  };

  const userRequestsFilter = (entity: any) => {
    const requester = entity.metadata.annotations?.['example.com/requested-by'];
    const approver = entity.metadata.annotations?.['example.com/approved-by'];
    const requestDate = entity.spec?.requestDate;
    const requestName = entity.metadata.name;

    // Platform admins can see all requests
    if (!isPlatformAdmin && 
        requester !== userName && 
        approver !== userName && 
        !visibleRequestsByDelegation.has(requestName)) {
      return false;
    }

    if (requestDate && !isWithinDateRange(requestDate, fromDate, toDate)) {
      return false;
    }

    const status = getRequestStatus(entity);
    if (statusFilter !== 'all' && status !== statusFilter) {
      return false;
    }

    return true;
  };

  return (
    <Page themeId="request">
      <Header title={t('requestPage.title')} subtitle={t('requestPage.subtitle')} />
      <ContentHeader>
        <SupportButton>{t('requestPage.supportButtonTitle')}</SupportButton>
      </ContentHeader>
      <Content noPadding>
        <EntityListProvider>
          {userName != null &&
            <>
              <Grid container direction="row" alignItems='center' justifyContent='flex-start' spacing={2} style={{ width: '100%' }}>
                <Grid item sm={3} xs={12}>
                  <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>{t('requestPage.filters.status.title')}</Typography>
                  <Select
                    value={statusFilter}
                    variant="outlined"
                    fullWidth
                    style={{ height: 40 }}
                    onChange={(e: any) => setStatusFilter(e.target.value)}
                  >
                    {requestStatus.map((option) => (
                      <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item sm={3} xs={12}>
                  <EntityTypePicker />
                </Grid>

                <Grid item sm={2} xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>{t('requestPage.filters.fromDate')}</Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={fromDate ?? ''}
                    variant='outlined'
                    fullWidth
                    onChange={e => setFromDate(e.target.value || null)}
                  />
                </Grid>

                <Grid item sm={2} xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>{t('requestPage.filters.toDate')}</Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={toDate ?? ''}
                    variant='outlined'
                    fullWidth
                    onChange={e => setToDate(e.target.value || null)}
                  />
                </Grid>

              </Grid>
              <PrefilteredTable
                kind="Request"
                title={t('requestPage.title') || 'Mis solicitudes'}
                userName={userName}
                filter={userRequestsFilter}
              />
            </>
          }
        </EntityListProvider>
      </Content>
    </Page>
  )
};